import { useCallback, useState } from 'react';
import { ResourceGroup } from 'types/schema';
import { signImageUpload } from 'utils/apis';

export interface ImageFile {
  id: string;
  file: File;
  previewUrl: string;
}

export type FileErrorType = 'invalidFormat' | 'tooLarge' | 'tooMany' | null;

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
const MAX_FILES = 3;

async function uploadFileToS3(file: File): Promise<string> {
  const ext = ALLOWED_TYPES[file.type] ?? 'jpg';
  const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  const signed: any = await signImageUpload({
    resourceGroup: ResourceGroup.QuickOrder,
    contentType: file.type,
    fileName: fileName,
  });
  if (!signed?.success || !signed.uploadUrl || !signed.fullUrl) {
    throw new Error('Sign upload failed');
  }
  await fetch(signed.uploadUrl, {
    method: 'PUT',
    mode: 'cors',
    body: file,
  });
  return signed.fullUrl;
}

function createImageFile(file: File): ImageFile {
  return {
    id: `${Date.now()}-${Math.random()}`,
    file: file,
    previewUrl: URL.createObjectURL(file),
  };
}

function validateFile(file: File): FileErrorType {
  if (!ALLOWED_TYPES.includes(file.type)) return 'invalidFormat';
  if (file.size > MAX_SIZE_BYTES) return 'tooLarge';
  return null;
}

export function useImageUpload() {
  const [images, setImages] = useState<ImageFile[]>([]);
  const [fileError, setFileError] = useState<FileErrorType>(null);

  const addFiles = useCallback((files: File[]) => {
    if (!files.length) return;

    let firstError: FileErrorType = null;
    const validFiles: File[] = [];

    for (const file of files) {
      const error = validateFile(file);
      if (error) {
        if (!firstError) firstError = error;
      } else {
        validFiles.push(file);
      }
    }

    setImages((prev) => {
      if (prev.length + validFiles.length > MAX_FILES) {
        firstError = 'tooMany';
        setFileError(firstError);
        return prev;
      }
      setFileError(firstError);
      return validFiles.length ? [...prev, ...validFiles.map(createImageFile)] : prev;
    });
  }, []);

  const removeImage = useCallback((id: string) => {
    setImages((prev) => {
      const target = prev.find((img) => img.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((img) => img.id !== id);
    });
  }, []);

  const clearImages = useCallback(() => {
    setImages((prev) => {
      prev.forEach((img) => URL.revokeObjectURL(img.previewUrl));
      return [];
    });
    setFileError(null);
  }, []);

  const clearFileError = useCallback(() => setFileError(null), []);

  const uploadAll = useCallback(async (): Promise<string[]> => {
    if (images.length === 0) return [];
    return Promise.all(images.map((img) => uploadFileToS3(img.file)));
  }, [images]);

  return {
    images: images,
    fileError: fileError,
    addFiles: addFiles,
    removeImage: removeImage,
    clearImages: clearImages,
    clearFileError: clearFileError,
    uploadAll: uploadAll,
  };
}
